/**
 * Role-Specific Procedures - WBC3 Appendix 8 Section 7.1 & SMS-Driven Requirements
 * 
 * Covers role-specific operational procedures and SMS obligations
 * Implements both direct WBC3 requirements and SMS-driven familiarisation
 */

import logger from 'utils/logger';

export const ROLE_SPECIFIC_PROCEDURES_ITEMS = [
  // UNIVERSAL JOB ROLE FAMILIARISATION
  {
    id: 'job_role_responsibilities',
    classification: 'MANDATORY',
    role: 'UNIVERSAL',
    text: 'Job Role and Responsibilities Familiarisation',
    description: 'Understanding of specific job role, duties, responsibilities, and company expectations',
    trigger: 'Always',
    reference: 'Training Appropriate to Tasks / Job Description SOP',
    tag: 'Role-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    smsRequired: true,
    rankVisibility: ['Master', 'Engineer', 'Crew', 'Crew Member', 'Cook', 'Steward', 'Supernumerary'],
    tooltip: 'Essential understanding of specific job role, duties, and responsibilities per job description and company SOPs.',
    sopReference: 'JD-{RANK}-001: {RANK} Job Description and Responsibilities'
  },

  // MASTER/OFFICER PROCEDURES
  {
    id: 'bridge_predeparture_checklist',
    classification: 'MANDATORY',
    role: 'DECK',
    text: 'Bridge Pre-Departure Checklist Procedures',
    description: 'Familiarisation with vessel-specific pre-departure checklist and master responsibilities',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Training Appropriate to Tasks / Master Responsibility',
    tag: 'Role-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    smsRequired: false,
    rankVisibility: ['Master', 'Officer'],
    tooltip: 'Essential understanding of vessel-specific pre-departure procedures and safety checks.',
    sopReference: 'SOP-NAV-001: Bridge Pre-Departure Checklist'
  },

  {
    id: 'navigation_equipment_checks',
    classification: 'MANDATORY',
    role: 'DECK',
    text: 'Navigation Equipment Check Procedures',
    description: 'Daily and pre-departure checks for navigation and communication equipment',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Training Appropriate to Tasks',
    tag: 'Role-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    smsRequired: false,
    rankVisibility: ['Master', 'Officer'],
    tooltip: 'Critical navigation equipment checks including GPS, radar, VHF, and emergency communication systems.'
  },

  {
    id: 'voyage_planning_procedures',
    classification: 'MANDATORY',
    role: 'DECK',
    text: 'Voyage Planning and Risk Assessment Procedures',
    description: 'Vessel-specific voyage planning procedures and passage risk assessment',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Risk Assessment / Master Responsibility',
    tag: 'Role-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    smsRequired: false,
    rankVisibility: ['Master', 'Officer'],
    tooltip: 'Voyage planning procedures including weather assessment, route planning, and risk evaluation.'
  },

  // ENGINEER PROCEDURES
  {
    id: 'engine_room_daily_checks',
    classification: 'MANDATORY',
    role: 'ENGINEERING',
    text: 'Engine Room Daily Check Procedures',
    description: 'Daily machinery inspection procedures and maintenance scheduling',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Training Appropriate to Tasks',
    tag: 'Role-Specific',
    priority: 'critical',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    smsRequired: false,
    naReasons: ['No dedicated engineer role', 'Master performs engine checks'],
    rankVisibility: ['Master', 'Engineer'],
    tooltip: 'Essential daily machinery checks including engine, pumps, electrical systems, and fuel management.',
    sopReference: 'SOP-ENG-001: Daily Engine Room Checks'
  },

  {
    id: 'fuel_management_procedures',
    classification: 'MANDATORY',
    role: 'ENGINEERING',
    text: 'Fuel Management and Bunkering Procedures',
    description: 'Safe fuel handling, bunkering procedures, and fuel system management',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Training Appropriate to Tasks',
    tag: 'Role-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    smsRequired: false,
    rankVisibility: ['Master', 'Engineer'],
    tooltip: 'Safe fuel handling including bunkering procedures, spill prevention, and fuel system operation.'
  },

  {
    id: 'maintenance_scheduling_procedures',
    classification: 'RECOMMENDED',
    role: 'ENGINEERING',
    text: 'Maintenance Scheduling and Record Keeping',
    description: 'Planned maintenance procedures and maintenance record documentation',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Operational Procedures / Training Appropriate to Tasks',
    tag: 'Role-Specific',
    priority: 'medium',
    verifiable: true,
    canBeNA: true,
    wbc3Required: false,
    smsRequired: true,
    naReasons: ['External maintenance contractor', 'Shore-based maintenance management'],
    rankVisibility: ['Master', 'Engineer'],
    tooltip: 'Maintenance planning and documentation procedures as required by vessel SMS.'
  },

  // DECK CREW PROCEDURES
  {
    id: 'deck_inspection_routines',
    classification: 'MANDATORY',
    role: 'DECK',
    text: 'Daily Deck Inspection Routines',
    description: 'Daily inspection procedures for deck equipment, safety equipment, and vessel condition',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Training Appropriate to Tasks',
    tag: 'Role-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    smsRequired: false,
    rankVisibility: ['Master', 'Crew', 'Crew Member'],
    tooltip: 'Daily inspection procedures for deck safety equipment and vessel condition monitoring.'
  },

  {
    id: 'mooring_station_procedures',
    classification: 'MANDATORY',
    role: 'DECK',
    text: 'Mooring Station Duties and Procedures',
    description: 'Specific mooring and unmooring duties including line handling and deck safety',
    trigger: 'Always', // Role-filtered via rankVisibility field
    reference: 'Training Appropriate to Tasks',
    tag: 'Role-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: true,
    smsRequired: false,
    rankVisibility: ['Master', 'Crew', 'Crew Member'],
    tooltip: 'Role-specific mooring duties including line handling, deck safety, and communication procedures.'
  },

  // SMS-DRIVEN OBLIGATIONS
  {
    id: 'hours_of_rest_policy',
    classification: 'RECOMMENDED',
    role: 'UNIVERSAL',
    text: 'Hours of Rest and Fatigue Management Policy',
    description: 'Company policy on work/rest limits, fatigue recognition, and reporting procedures',
    trigger: 'Always',
    reference: 'SMS Operational Procedures',
    tag: 'SMS-Driven',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    smsRequired: true,
    rankVisibility: ['Master', 'Engineer', 'Crew', 'Crew Member', 'Supernumerary'],
    tooltip: 'Understanding work/rest limits and fatigue risks per company SMS policy. Required for crew fitness assessments.',
    sopReference: 'SOP-SMS-001: Hours of Rest and Fatigue Management Policy'
  },

  {
    id: 'incident_reporting_procedures',
    classification: 'RECOMMENDED',
    role: 'UNIVERSAL',
    text: 'Incident and Near Miss Reporting Procedures',
    description: 'Company procedures for reporting incidents, near misses, and safety concerns',
    trigger: 'Always',
    reference: 'SMS Operational Procedures / Risk Assessment',
    tag: 'SMS-Driven',
    priority: 'high',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    smsRequired: true,
    rankVisibility: ['Master', 'Engineer', 'Crew', 'Crew Member', 'Supernumerary'],
    tooltip: 'Company incident reporting procedures including near misses, injuries, and equipment failures.'
  },

  {
    id: 'company_safety_policy',
    classification: 'RECOMMENDED',
    role: 'UNIVERSAL',
    text: 'Company Safety Policy and SMS Overview',
    description: 'Overview of company Safety Management System and safety policy requirements',
    trigger: 'Always',
    reference: 'SMS Operational Procedures',
    tag: 'SMS-Driven',
    priority: 'medium',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    smsRequired: true,
    rankVisibility: ['Master', 'Engineer', 'Crew', 'Crew Member', 'Supernumerary'],
    tooltip: 'Understanding company safety management system and individual safety responsibilities.'
  },

  {
    id: 'environmental_procedures',
    classification: 'RECOMMENDED',
    role: 'UNIVERSAL',
    text: 'Environmental Protection and Waste Management',
    description: 'Company environmental policy including waste management and pollution prevention',
    trigger: 'Always',
    reference: 'SMS Operational Procedures / Risk Assessment',
    tag: 'SMS-Driven',
    priority: 'medium',
    verifiable: true,
    canBeNA: false,
    wbc3Required: false,
    smsRequired: true,
    rankVisibility: ['Master', 'Engineer', 'Crew', 'Crew Member', 'Supernumerary'],
    tooltip: 'Environmental protection procedures including waste disposal, bilge water, and MARPOL compliance.'
  },

  // COOK/STEWARD PROCEDURES (if applicable)
  {
    id: 'galley_safety_procedures',
    classification: 'MANDATORY',
    role: 'CATERING',
    text: 'Galley Safety and Food Hygiene Procedures',
    description: 'Galley safety procedures including fire prevention and food hygiene requirements',
    trigger: 'Always', // Role-filtered via rankVisibility field - shows for vessels with galley staff
    reference: 'Training Appropriate to Tasks',
    tag: 'Role-Specific',
    priority: 'high',
    verifiable: true,
    canBeNA: true,
    wbc3Required: true,
    smsRequired: false,
    naReasons: ['No galley facilities', 'No cooking operations', 'Shore-based catering only'],
    rankVisibility: ['Master', 'Cook', 'Steward'],
    tooltip: 'Galley fire safety, food hygiene, and safe cooking procedures for vessel operations.'
  }
];

/**
 * Generate Role-Specific Procedures checklist
 * WBC3-compliant role-based familiarisation per Appendix 8, Section 7.1
 * Includes SMS-driven obligations per Section 3, Section 6, Section 12
 */
export function generateRoleSpecificProceduresChecklist(vessel, crew) {
  const items = [];
  
  // Default crew data if not provided
  const defaultCrew = crew || { rank: 'Master' };
  
  ROLE_SPECIFIC_PROCEDURES_ITEMS.forEach(item => {
    // Check trigger condition
    let includeItem = false;
    
    if (item.trigger === 'Always') {
      includeItem = true;
    } else if (typeof item.trigger === 'function') {
      try {
        includeItem = item.trigger(vessel, defaultCrew);
      } catch (error) {
        logger.warn('Error in role trigger:', item.id, error);
        includeItem = true; // Default to showing item if error
      }
    }
    
    if (includeItem) {
      // Process SOP reference with rank substitution
      let processedItem = { ...item };
      
      if (item.sopReference && defaultCrew.rank) {
        processedItem.sopReference = item.sopReference
          .replace(/\{RANK\}/g, defaultCrew.rank);
      }
      
      items.push(processedItem);
    }
  });
  
  return {
    section: 'role_specific_procedures',
    title: 'Role-Specific Procedures',
    items: items,
    completionRequired: true,
    auditTrail: true,
    description: 'Role-specific operational procedures per WBC3 Appendix 8, Section 7.1 and SMS-driven obligations'
  };
}

// Role-specific procedures completion rules
export const ROLE_SPECIFIC_RULES = {
  // Required for operational competency
  required_for_operations: true,
  
  // Target completion time
  target_completion_minutes: 60,
  
  // Blocks operations until key items complete
  blocks_operations: true, // For critical safety items
  
  // Applies based on crew role
  role_based_visibility: true,
  
  // Integration with other systems
  integration: {
    crew_management: 'Links to crew role assignments',
    sms_documentation: 'Integrates with SMS policy documents',
    incident_reporting: 'Links to incident management system'
  },
  
  // Audit requirements
  audit: {
    role_specific_completion: true,
    sms_compliance_tracking: true,
    master_sign_off_required: true,
    completion_timestamp: true,
    annual_review_required: true // For SMS items
  }
};